import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { forwardRef } from 'preact/compat';
import { useContext } from 'preact/hooks';

import { PollContext } from '../../../potg/potg.component';

import { getImageUrl, onErrorImage } from '../../../helpers/images';

import MVPIcon from '../../shared-components/icons/MVPIcon';

import pollStyleSheet from '../poll.module.scss';
const pollStyles = pollStyleSheet.locals || {};

/**
 * PollWinner component
 * Displays the winner of the poll as well as the user's vote if applicable.
 *
 * @param {object} props - Component properties
 * @param {object} props.winningVotes - The list of winning votes
 * @param {object} props.userVote - The user's vote object
 * @param {boolean} props.showCount - Whether to show the count of votes
 */
const PollWinner = forwardRef(
    ({ winningVotes = [], userVote, showCount }, ref) => {
        const { t } = useTranslation();
        const { hasOptionImages } = useContext(PollContext);

        const hasMultipleWinners = winningVotes.length > 1;
        const firstWinningVote = winningVotes[0];

        const singleWinner = (
            <div data-testid="poll-winner-single">
                {!firstWinningVote.image_url &&
                    firstWinningVote.external_image_url && (
                        <img
                            part="potg-winner-image"
                            src={firstWinningVote.external_image_url}
                            alt={`${firstWinningVote.text}`}
                            onError={onErrorImage}
                            className={pollStyles.pollWinnerImg}
                            width="100%"
                        />
                    )}
                {/* eslint-disable no-magic-numbers */}
                {firstWinningVote.image_url && (
                    <img
                        part="potg-winner-image"
                        src={getImageUrl(firstWinningVote.image_url, 496, 372)}
                        alt={`${firstWinningVote.text}`}
                        onError={onErrorImage}
                        className={pollStyles.pollWinnerImg}
                        width="100%"
                    />
                )}
                {/* eslint-enable no-magic-numbers */}
                <div
                    part="potg-winner-text"
                    className={pollStyles.pollWinnerText}
                >
                    {firstWinningVote.text}
                </div>
            </div>
        );

        const winnersList = (
            <ul
                part="potg-winner"
                className={pollStyles.pollWinnerList}
                data-testid="poll-winners-list"
            >
                {winningVotes.map((vote) => (
                    <li
                        key={vote.option_id}
                        className={classNames(pollStyles.pollWinnerListItem, {
                            [pollStyles.pollWinnerListItemNoImage]:
                                !hasOptionImages
                        })}
                    >
                        {!vote.image_url && vote.external_image_url && (
                            <img
                                src={vote.external_image_url}
                                alt={`${vote.text}`}
                                onError={onErrorImage}
                                className={pollStyles.pollWinnerListItemImg}
                            />
                        )}
                        {/* eslint-disable no-magic-numbers */}
                        {vote.image_url && (
                            <img
                                src={getImageUrl(vote.image_url, 70, 52)}
                                alt={`${vote.text}`}
                                onError={onErrorImage}
                                className={pollStyles.pollWinnerListItemImg}
                            />
                        )}
                        {/* eslint-enable no-magic-numbers */}
                        <span className={pollStyles.pollWinnerListItemText}>
                            {vote.text}
                        </span>
                    </li>
                ))}
            </ul>
        );

        return (
            <div className={pollStyles.pollWinner} ref={ref}>
                <div className={pollStyles.pollWinnerMvpIconWrapper}>
                    <MVPIcon className={pollStyles.pollWinnerMvpIcon} />
                    <span className={pollStyles.pollHeadlineSmall}>
                        {hasMultipleWinners
                            ? t('polling.theWinnersAre')
                            : t('polling.theWinnerIs')}
                    </span>
                </div>
                {hasMultipleWinners ? winnersList : singleWinner}
                {userVote && (
                    <div className={pollStyles.pollYourVoteSmall}>
                        <div className={pollStyles.pollHeadlineSmall}>
                            {t('polling.votedFor')}
                        </div>
                        <ol className={pollStyles.pollResultsList}>
                            <li
                                className={classNames(
                                    pollStyles.pollResultsListItem,
                                    pollStyles.pollResultsListItemYourVote,
                                    {
                                        [pollStyles.pollResultsListItemWinner]:
                                            userVote.option_id ===
                                            firstWinningVote.option_id
                                    }
                                )}
                                key={userVote.option_id}
                            >
                                <div
                                    className={classNames(
                                        pollStyles.pollResultsListItemInner,
                                        pollStyles.pollResultsListItemInnerSingle,
                                        pollStyles.pollResultsListItemInnerYourVote
                                    )}
                                >
                                    <div>
                                        {!userVote.image_url &&
                                            userVote.external_image_url && (
                                                <img
                                                    src={
                                                        userVote.external_image_url
                                                    }
                                                    onError={onErrorImage}
                                                    alt={`${userVote.text}`}
                                                    className={
                                                        pollStyles.pollResultsListItemImg
                                                    }
                                                    width="70"
                                                />
                                            )}
                                        {/* eslint-disable no-magic-numbers */}
                                        {userVote.image_url && (
                                            <img
                                                src={getImageUrl(
                                                    userVote.image_url,
                                                    70,
                                                    52
                                                )}
                                                onError={onErrorImage}
                                                alt={`${userVote.text}`}
                                                className={
                                                    pollStyles.pollResultsListItemImg
                                                }
                                                width="70"
                                            />
                                        )}
                                        {/* eslint-enable no-magic-numbers */}
                                    </div>
                                    <div className={pollStyles.pollResultsMeta}>
                                        <div>{userVote.text}</div>
                                        <div
                                            className={
                                                pollStyles.pollResultsPercentageBar
                                            }
                                        >
                                            <div
                                                style={{
                                                    width: `${userVote.percentage}%`
                                                }}
                                                className={
                                                    pollStyles.pollResultsPercentageBarFill
                                                }
                                            />
                                        </div>
                                        <div>
                                            <span
                                                className={
                                                    pollStyles.pollResultsMetaPercentage
                                                }
                                            >
                                                {userVote.percentage}%
                                            </span>{' '}
                                            {showCount && (
                                                <span
                                                    className={
                                                        pollStyles.pollResultsMetaVotes
                                                    }
                                                >
                                                    {userVote.times_selected}{' '}
                                                    {t('common.votes')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ol>
                    </div>
                )}
            </div>
        );
    }
);

PollWinner.propTypes = {
    winningVotes: PropTypes.array,
    userVote: PropTypes.object,
    showCount: PropTypes.bool
};

PollWinner.displayName = 'PollWinner';

export default PollWinner;
